import { useState } from 'react';
import useGraphQLRequest from './useGraphQLRequest';
import { CHECK_USER_COORDINATES } from '@mp/graphql';
import { useNetworkRequest } from './useNetworkRequest';
import { JobStatus } from '@mp/graphql/generated/graphql';

type CheckUserCoordinatesOptions = {
  jobId?: number | null;
  latitude: number;
  longitude: number;
  action?: 'clock_in' | 'clock_out';
  silentClockInOut?: boolean;
};

const SILENT_CLOCK_IN_OUT_THROTTLE_MS = 60_000;
const lastSilentClockInOutAt = new Map<string, number>();

export const useJobGeoLocation = () => {
  const [isCheckingUserCoordinates, setIsCheckingUserCoordinates] = useState(false);
  const { request } = useGraphQLRequest();
  const { axios } = useNetworkRequest();

  const postSilentClockIn = async ({
    action,
    latitude,
    longitude,
    jobId,
  }: {
    action: string;
    latitude: number;
    longitude: number;
    jobId?: number | null;
  }) => {
    const throttleKey = `${jobId}:${action}`;
    const lastSentAt = lastSilentClockInOutAt.get(throttleKey);
    if (lastSentAt && Date.now() - lastSentAt < SILENT_CLOCK_IN_OUT_THROTTLE_MS) {
      return;
    }
    lastSilentClockInOutAt.set(throttleKey, Date.now());

    const attrs = {
      approved_clock_in: action === 'clock_in' ? new Date() : undefined,
      approved_clock_out: action === 'clock_out' ? new Date() : undefined,
      manual_override: false,
      lat: latitude,
      long: longitude,
    };

    await axios.post(`/v3/professional/jobs/${jobId}/${action}`, attrs);
  };

  const logClockInOut = async (
    jobId?: number | null,
    action?: 'clock_in' | 'clock_out',
  ): Promise<void> => {
    const attrs = {
      approved_clock_in: action === 'clock_in' ? new Date() : undefined,
      approved_clock_out: action === 'clock_out' ? new Date() : undefined,
      manual_override: false,
      lat: 0,
      long: 0,
    };

    await axios.post(`/v3/professional/jobs/${jobId}/${action}`, attrs);
  };

  const checkUserCoordinates = async ({
    jobId,
    latitude,
    longitude,
    silentClockInOut = false,
    action,
  }: CheckUserCoordinatesOptions) => {
    try {
      setIsCheckingUserCoordinates(true);

      const response = (await request(CHECK_USER_COORDINATES, {
        id: jobId,
        latitude,
        longitude,
      })) as { job: { location: { in_geo_fence_range: boolean }; status: JobStatus } };

      const isInGeofenceRange = response.job.location.in_geo_fence_range;
      const isShiftClockedIn = response.job.status === JobStatus.ClockedIn;
      const isShiftBooked = response.job.status === JobStatus.Booked;

      if (!isInGeofenceRange && silentClockInOut && action) {
        if (
          (action === 'clock_in' && isShiftBooked) ||
          (action === 'clock_out' && isShiftClockedIn)
        ) {
          await postSilentClockIn({ action, latitude, longitude, jobId });
        }
      }
      setIsCheckingUserCoordinates(false);
      return isInGeofenceRange;
    } catch {
      setIsCheckingUserCoordinates(false);
      return false;
    }
  };

  return { logClockInOut, isCheckingUserCoordinates, checkUserCoordinates };
};
